import { useMemo } from 'react'
import { capitalizeFirstWord } from 'lib/util/string'
import useAppData from './useAppData'

/**
 * Describe accessory compatibility hook
 *
 * @param {string} faction - the faction (type)
 * @param {string} persona - the persona (gender)
 * @param {string} style - the style (E.g. puks, manga)
 * @param {string} color - the color
 * @returns {object} the context object
 */
export default function useDescribeAccessoryCompatibility (
  faction,
  persona,
  style,
  color
) {
  const appData = useAppData()

  /**
   * The gender list
   */
  const personaList = useMemo(
    () =>
      persona.flatMap(value => {
        const itemPersona = appData?.personas?.[value]

        if (
          !style ||
          !style.length ||
          style.length === Object.keys(itemPersona.style).length ||
          Object.keys(itemPersona.style).length === 1
        ) {
          return itemPersona?.title
        }

        return style.map(value => itemPersona?.style?.[value]?.title)
      }),
    [appData, persona, style]
  )

  const labelPersonaListAmpersand = useMemo(
    () => personaList.join(' & '),
    [personaList]
  )

  const labelPersonaListComma = useMemo(
    () => personaList.join(', '),
    [personaList]
  )

  const labelFaction = useMemo(
    () => appData?.types?.[faction]?.title || 'All',
    [faction, appData]
  )

  /**
   * The color label
   */
  const labelColor = useMemo(
    () => (color ? capitalizeFirstWord(color) : ''),
    [color]
  )

  /**
   * Memo: The descriptive compatibility
   */
  const labelDescriptiveCompatibilityAmpersand = useMemo(() => {
    if (!personaList.length) {
      if (labelFaction === 'All') {
        return <>all</>
      }

      return (
        <>
          only compatible with <strong>{labelFaction}</strong>
        </>
      )
    }

    if (labelFaction === 'All') {
      if (!personaList.length) {
        return (
          <>
            compatible with <strong>all</strong>
          </>
        )
      }

      return (
        <>
          only compatible with{' '}
          <strong>
            {labelPersonaListAmpersand}
            {color ? <> ({labelColor})</> : null}
          </strong>
        </>
      )
    }

    return (
      <>
        only compatible with{' '}
        <strong>
          {labelFaction}: {labelPersonaListAmpersand}
          {color ? <> ({labelColor})</> : null}
        </strong>
      </>
    )
  }, [personaList, labelFaction, labelPersonaListAmpersand, labelColor])

  /**
   * Memo: The descriptive compatibility
   */
  const labelDescriptiveCompatibilityList = useMemo(() => {
    if (!personaList.length) {
      if (labelFaction === 'All') {
        return <strong>all</strong>
      }

      return (
        <>
          only compatible with <strong>{labelFaction}</strong>
        </>
      )
    }

    if (labelFaction === 'All') {
      if (!personaList.length) {
        return (
          <>
            compatible with <strong>all</strong>
          </>
        )
      }

      return (
        <>
          only compatible with{' '}
          <strong>
            {labelPersonaListAmpersand}
            {color ? <> ({labelColor})</> : null}
          </strong>
        </>
      )
    }

    return (
      <>
        only compatible with{' '}
        <strong>
          {labelFaction}: {labelPersonaListComma}
          {color ? <> ({labelColor})</> : null}
        </strong>
      </>
    )
  }, [
    personaList,
    labelFaction,
    labelPersonaListComma,
    labelPersonaListAmpersand,
    labelColor
  ])

  const labelPersonaCompatibilityShorthand = useMemo(() => {
    if (!personaList.length) {
      // No personas, it fits all
      return <>All</>
    }

    if (personaList.length > 1) {
      // More than one persona
      return <>Multi</>
    }

    return (
      <>
        {labelPersonaListComma}
        {color ? <> ({labelColor})</> : null}
      </>
    )
  }, [personaList, labelPersonaListComma, labelColor])

  const labelPersonaCompatibilityList = useMemo(() => {
    if (!personaList.length) {
      return <>All</>
    }

    return { labelPersonaListComma }
  }, [personaList, labelPersonaListComma])

  const labelPersonaCompatibilityAmpersand = useMemo(() => {
    if (!personaList.length) {
      return <>All</>
    }

    return { labelPersonaListComma }
  }, [personaList, labelPersonaListAmpersand])

  /**
   * Memo: The descriptive compatibility
   */
  const labelCompatibilityList = useMemo(() => {
    if (!personaList.length) {
      if (labelFaction === 'All') {
        return <strong>all</strong>
      }

      return <strong>{labelFaction}</strong>
    }

    if (labelFaction === 'All') {
      if (!personaList.length) {
        return <strong>All</strong>
      }

      return (
        <strong>
          {labelPersonaListComma}
          {color ? <> ({labelColor})</> : null}
        </strong>
      )
    }

    return (
      <strong>
        {labelFaction}: {labelPersonaListComma}
        {color ? <> ({labelColor})</> : null}
      </strong>
    )
  }, [personaList, labelFaction, labelPersonaListComma, labelColor])

  /**
   * Memo: The context object
   */
  const ctx = useMemo(
    () => ({
      personaList,
      labelPersonaListAmpersand,
      labelPersonaListComma,
      labelFaction,
      labelColor,
      labelDescriptiveCompatibilityAmpersand,
      labelDescriptiveCompatibilityList,
      labelCompatibilityList,
      labelPersonaCompatibilityShorthand,
      labelPersonaCompatibilityList,
      labelPersonaCompatibilityAmpersand
    }),
    [
      personaList,
      labelPersonaListAmpersand,
      labelPersonaListComma,
      labelFaction,
      labelColor,
      labelDescriptiveCompatibilityAmpersand,
      labelDescriptiveCompatibilityList,
      labelCompatibilityList,
      labelPersonaCompatibilityShorthand,
      labelPersonaCompatibilityList,
      labelPersonaCompatibilityAmpersand
    ]
  )

  return ctx
}
